// "+ Add a piece" — the structured form of an op that already existed.
//
// The button used to open the same generic "Describe a change…" box as
// "Adjust the split…": an affordance promising a structured action, delivering
// a prompt box with no hint what to type, routed through a model turn. But
// `add_child` is on `clientOpSchema` and the SERVER's pure reducer executes it
// deterministically — title, scope line, and the criterion ids the new piece
// takes over. It had no sender. This is it.
//
// The three inputs are exactly the reducer's three arguments, so nothing here
// is interpreted: what the user types is what gets written, and the criteria
// they tick move off whoever holds them now (the reducer does that itself —
// "add a piece for AC4" must not silently produce an overlap).
//
// Two client-side guards, both computable from data already on screen:
//   - at least one must-have, because a piece with nothing to deliver is
//     refused (`empty_child`) and the reducer's own criterionIds min is 1;
//   - no sibling stranded, the same check `RemoveChildDialog` runs — taking a
//     piece's last must-have empties it, and that deny would name a piece the
//     user never touched.

import { useT } from "../i18n";
import { useRemountSafeState } from "../state/remount-safe-state";
import { AutoGrowTextarea } from "../ui/AutoGrowTextarea";

import { CHILD_SCOPE_MAX, CHILD_TITLE_MAX } from "@loom/shared/readiness";

export interface AddChildDialogProps {
  /** The parent's must-have list, in card order (index = the AC ordinal - 1). */
  readonly criteria: ReadonlyArray<string>;
  /** The pieces that exist now — needed to say who holds each must-have, and to
   *  refuse a pick that would empty one of them. */
  readonly siblings: ReadonlyArray<{
    readonly title: string;
    readonly coveredCriteria: ReadonlyArray<number>;
  }>;
  /** Same convention as `BreakdownCard`'s own `stateKey`: stable across a
   *  step-key remount (an ask preempting `breakdown_review` unmounts this
   *  whole subtree) so the half-composed piece survives round-tripping
   *  through an unrelated ask, mirroring `adjustDraft`. */
  readonly stateKey: string;
  readonly onCancel: () => void;
  /** Zero-based criterion indices; the caller maps them to `AC{n}` ids. */
  readonly onConfirm: (piece: {
    readonly title: string;
    readonly description: string;
    readonly criterionIndices: ReadonlyArray<number>;
  }) => void;
}

export function AddChildDialog({
  criteria,
  siblings,
  stateKey,
  onCancel,
  onConfirm,
}: AddChildDialogProps) {
  const t = useT();
  const [title, setTitle] = useRemountSafeState<string>(
    `bd:add:title:${stateKey}`,
    "",
  );
  const [description, setDescription] = useRemountSafeState<string>(
    `bd:add:desc:${stateKey}`,
    "",
  );
  const [picked, setPicked] = useRemountSafeState<ReadonlySet<number>>(
    `bd:add:picked:${stateKey}`,
    () => new Set(),
  );

  // Same normalization the rename does, and for the same reason: a GitLab issue
  // title is single-line by contract and Loom composes two markdown rows out of
  // it. The server re-applies this (it is the enforcement); doing it here keeps
  // the character budget the user sees honest.
  const cleanTitle = title.replace(/\s+/gu, " ").trim().slice(0, CHILD_TITLE_MAX);
  const cleanScope = description.trim().slice(0, CHILD_SCOPE_MAX);

  const stranded = siblings.filter(
    (s) => s.coveredCriteria.filter((i) => !picked.has(i)).length === 0,
  );
  const confirmable =
    cleanTitle.length > 0 &&
    cleanScope.length > 0 &&
    picked.size > 0 &&
    stranded.length === 0;

  const ownerOf = (index: number): string | undefined =>
    siblings.find((s) => s.coveredCriteria.includes(index))?.title;

  // `title` / `description` / `picked` are REMOUNT-safe on purpose (see the
  // header comment on `stateKey`) so an involuntary remount — a sandbox
  // question preempting this screen — doesn't erase a half-composed piece.
  // An explicit Cancel is a different gesture: the user is discarding the
  // draft, not surviving an interruption, so this is the one path that clears
  // the remount-safe entries rather than leaving them for the next open. Live,
  // reopening after Cancel restored the exact name, scope and tick — including
  // an already-unsubmittable state from `addPieceImpossible` above.
  const cancelAndDiscard = (): void => {
    setTitle("");
    setDescription("");
    setPicked(new Set());
    onCancel();
  };

  return (
    <div
      className="bd-add-form"
      role="group"
      aria-label={t("breakdown.add.title")}
    >
      <p className="bd-remove-head">{t("breakdown.add.title")}</p>
      <p className="bd-remove-note">{t("breakdown.add.help")}</p>
      <label className="bd-add-field">
        <span className="bd-add-label">{t("breakdown.add.name")}</span>
        <input
          type="text"
          className="bd-name-edit"
          autoFocus
          value={title}
          maxLength={CHILD_TITLE_MAX}
          placeholder={t("breakdown.add.namePlaceholder")}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            // Single-line by contract — plain Enter cannot insert a newline
            // here, so it moves on rather than submitting a half-filled form.
            if (e.key === "Enter") e.preventDefault();
            else if (e.key === "Escape") cancelAndDiscard();
          }}
        />
      </label>
      <label className="bd-add-field">
        <span className="bd-add-label">{t("breakdown.add.scope")}</span>
        <AutoGrowTextarea
          className="bd-adjust-input"
          value={description}
          maxLength={CHILD_SCOPE_MAX}
          placeholder={t("breakdown.add.scopePlaceholder")}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") cancelAndDiscard();
          }}
        />
      </label>
      <fieldset className="bd-add-crits">
        <legend className="bd-add-label">{t("breakdown.add.criteria")}</legend>
        <ul className="bd-remove-list">
          {criteria.map((text, index) => {
            const owner = ownerOf(index);
            return (
              <li key={`${String(index)}:${text}`}>
                <label className="bd-add-crit">
                  <input
                    type="checkbox"
                    checked={picked.has(index)}
                    onChange={(e) =>
                      setPicked((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(index);
                        else next.delete(index);
                        return next;
                      })
                    }
                  />
                  <span className="bd-remove-crit">{text}</span>
                  {owner !== undefined && (
                    <span className="bd-add-owner">
                      {t("breakdown.add.ownedBy", { title: owner })}
                    </span>
                  )}
                </label>
              </li>
            );
          })}
        </ul>
      </fieldset>
      {picked.size === 0 && (
        <p className="bd-remove-note">{t("breakdown.add.criteriaRequired")}</p>
      )}
      {stranded.length > 0 && (
        <p className="bd-remove-note is-warn">
          {t("breakdown.remove.strands", {
            titles: stranded.map((s) => `"${s.title}"`).join(", "),
          })}
        </p>
      )}
      <div className="bd-remove-actions">
        <button
          type="button"
          className="bd-btn is-ghost"
          onClick={cancelAndDiscard}
        >
          {t("breakdown.cancel")}
        </button>
        <button
          type="button"
          className="bd-btn is-primary"
          disabled={!confirmable}
          onClick={() =>
            onConfirm({
              title: cleanTitle,
              description: cleanScope,
              criterionIndices: [...picked].sort((a, b) => a - b),
            })
          }
        >
          {t("breakdown.add.confirm")}
        </button>
      </div>
    </div>
  );
}
